import {
  type HttpEvent,
  type HttpHandlerFn,
  type HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import {
  type EnvironmentProviders,
  inject,
  InjectionToken,
  makeEnvironmentProviders,
} from '@angular/core';
import type { RequestHandler } from 'msw';
import { defer, type Observable, switchMap } from 'rxjs';

/**
 * Internal injection token holding the {@link RequestHandler} list
 * consumed by {@link mockBackendInterceptor}.
 */
const MOCK_BACKEND_HANDLERS = new InjectionToken<readonly RequestHandler[]>(
  'MOCK_BACKEND_HANDLERS'
);

function cryptoRandomId(): string {
  return crypto.randomUUID();
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === '') {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Attempts to run an MSW {@link RequestHandler} against a synthetic
 * Fetch request built from the Angular HTTP request URL + method.
 * Returns the MSW `Response` or `null` when the handler did not match.
 */
async function tryRun(
  handler: RequestHandler,
  url: string,
  method: string
): Promise<Response | null> {
  const request = new Request(url, { method });
  // MSW does not expose the internal `run` signature; we narrow into it to drive matching from the interceptor.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const runner = handler as unknown as {
    run: (ctx: {
      request: Request;
      requestId: string;
    }) => Promise<{ response?: Response } | null>;
  };
  const result = await runner.run({ request, requestId: cryptoRandomId() });
  return result?.response ?? null;
}

/**
 * Lightweight {@link HttpInterceptorFn} that resolves Angular HTTP
 * requests against a list of MSW {@link RequestHandler}s — without
 * standing up a service worker or `msw/node` server.
 *
 * Trade-offs vs `setupMockServerLifecycle`:
 *
 * - **Pros:** zero global setup, no per-suite teardown, runs anywhere
 *   `TestBed` runs (including the browser-mode component specs that
 *   today can't easily host `msw/node`).
 * - **Cons:** only intercepts requests that go through Angular's
 *   `HttpClient` — direct `fetch` calls bypass it; relies on MSW's
 *   internal `run()` shape and is therefore best for happy-path stubs.
 *
 * Pair with {@link provideMockBackendInterceptor}.
 *
 * @example
 * TestBed.configureTestingModule({
 *   providers: [
 *     provideHttpClient(withInterceptors([mockBackendInterceptor])),
 *     provideMockBackendInterceptor(factoryHandlers('http://test/api/v1')),
 *   ],
 * });
 */
export const mockBackendInterceptor: HttpInterceptorFn = (
  req,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const handlers = inject(MOCK_BACKEND_HANDLERS, { optional: true }) ?? [];
  return defer(async () => {
    for (const handler of handlers) {
      const response = await tryRun(handler, req.url, req.method);
      if (response !== null) {
        const body = await readBody(response);
        return new HttpResponse({
          body,
          status: response.status,
          statusText: response.statusText,
        });
      }
    }
    return null;
  }).pipe(switchMap((mocked) => (mocked === null ? next(req) : [mocked])));
};

/**
 * Provides the handler list consumed by {@link mockBackendInterceptor}.
 *
 * Register both the interceptor and this provider; the interceptor is
 * a no-op without it.
 */
export function provideMockBackendInterceptor(
  handlers: RequestHandler[]
): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: MOCK_BACKEND_HANDLERS, useValue: handlers },
  ]);
}
